
import React from 'react';
import { LocalizedContent } from '../utils/localization';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '@/components/ui/accordion';
import { motion } from 'framer-motion';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Info } from 'lucide-react';

interface FAQProps {
  content: LocalizedContent;
}

const FAQ: React.FC<FAQProps> = ({ content }) => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <section className="py-16 relative overflow-hidden">
      {/* Background video - optimized with loading attribute */}
      <div className="absolute inset-0 w-full h-full z-0">
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
          <iframe 
            src="https://streamable.com/e/fgtx2p?autoplay=1&nocontrols=1&muted=1&loop=1" 
            allow="fullscreen;autoplay" 
            allowFullScreen
            style={{ border: 'none', width: '100%', height: '100%', position: 'absolute', left: '0px', top: '0px', overflow: 'hidden', objectFit: 'cover' }}
            title="FAQ Background"
            loading="lazy"
          ></iframe>
        </div>
      </div>
      
      {/* Dark overlay for better text readability */}
      <div className="absolute inset-0 bg-black/70 z-10"></div>
      
      {/* Content */}
      <div className="container mx-auto px-4 relative z-20">
        <motion.div 
          className="max-w-4xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.h2 
            className="text-3xl font-bold text-center mb-12 text-white font-future"
            variants={itemVariants}
          >
            {content.faqTitle}
          </motion.h2>
          
          <Accordion type="single" collapsible className="space-y-6">
            {content.faqs.map((faq, index) => (
              <motion.div key={index} variants={itemVariants}>
                <AccordionItem 
                  value={`item-${index}`} 
                  className="bg-[#15151D]/80 backdrop-blur-sm p-6 rounded-lg border border-purple-900/20 group hover:bg-[#15151D]/90 transition-all duration-300"
                >
                  <AccordionTrigger className="text-xl font-semibold text-white group-hover:text-purple-400 transition-colors">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pt-3 animate-accordion-down">
                    <div className="flex gap-2 items-start">
                      <div className="bg-purple-900/20 rounded-full p-0.5 h-6 w-6 flex items-center justify-center mt-1">
                        <Info className="h-4 w-4 text-purple-400" />
                      </div>
                      <p>{faq.answer}</p>
                    </div>

                    {/* Additional related information with HoverCard for enhanced interaction */}
                    {index === 0 && (
                      <div className="mt-4 pt-4 border-t border-purple-900/20">
                        <HoverCard>
                          <HoverCardTrigger asChild>
                            <button className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1">
                              <Info className="h-4 w-4" /> Weiterführende Informationen
                            </button>
                          </HoverCardTrigger>
                          <HoverCardContent className="w-80 bg-[#15151D]/95 border-purple-900/40 text-white">
                            <div className="space-y-2">
                              <h4 className="text-sm font-medium text-purple-400">Gut zu wissen</h4>
                              <p className="text-xs text-gray-300">
                                Unsere KI-Videos werden mit modernster Technologie erstellt und können jederzeit an Ihre spezifischen Bedürfnisse angepasst werden.
                              </p>
                            </div>
                          </HoverCardContent>
                        </HoverCard>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>

          {/* Additional FAQ content with enhanced microinteractions */}
          <motion.div 
            className="mt-12 pt-8 border-t border-purple-900/20 text-center"
            variants={itemVariants}
          >
            <Collapsible className="w-full">
              <CollapsibleTrigger className="text-white hover:text-purple-400 transition-colors flex items-center gap-2 mx-auto">
                <span className="text-sm font-medium">Noch mehr Fragen?</span>
                <span className="text-xs bg-purple-900/30 px-2 py-0.5 rounded-full">+3</span>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-6 space-y-4">
                <div className="bg-[#15151D]/80 backdrop-blur-sm p-5 rounded-lg border border-purple-900/20">
                  <h3 className="text-lg font-medium text-white mb-2">Kann ich meine Videos später ändern lassen?</h3>
                  <p className="text-gray-400">Ja, alle unsere Videopakete beinhalten die Möglichkeit, Änderungen vorzunehmen. Nachträgliche Anpassungen können jederzeit gegen eine geringe Gebühr vorgenommen werden.</p>
                </div>
                <div className="bg-[#15151D]/80 backdrop-blur-sm p-5 rounded-lg border border-purple-900/20">
                  <h3 className="text-lg font-medium text-white mb-2">Wie werden die Videos geliefert?</h3>
                  <p className="text-gray-400">Sie erhalten alle Ihre Videos in Full HD (1080p) via Download-Link. Auf Wunsch können wir auch 4K-Auflösung oder spezielle Kodierungen für bestimmte Plattformen anbieten.</p>
                </div>
                <div className="bg-[#15151D]/80 backdrop-blur-sm p-5 rounded-lg border border-purple-900/20">
                  <h3 className="text-lg font-medium text-white mb-2">Welche Zahlungsarten akzeptieren Sie?</h3>
                  <p className="text-gray-400">Wir akzeptieren alle gängigen Zahlungsmethoden: Kreditkarten, PayPal, Überweisung und auf Rechnung (für Firmenkunden nach Bonitätsprüfung).</p>
                </div>
              </CollapsibleContent>
            </Collapsible>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
